import { logGreyError } from 'owa-analytics';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

import { db, placesPeopleTable, peopleTimezoneSettingsTable } from '../database';

// De-dupes concurrent maintenance runs, keyed by owner + timezone. The 3 cache hooks
// each trigger maintenance on mount, so without this guard multiple runs could read a
// stale timezone and clear cache that another run (or the service) has already refreshed.
// The key MUST include the owner so an account switch (different owner) is never skipped,
// and the timezone so a timezone change for the same owner is still processed. Each entry
// is removed on completion so later changes in the same session can self-heal.
const maintenanceInFlightByKey = new Map<string, Promise<void>>();

/**
 * Clears the given owner's cached people data when the timezone it was stored under no
 * longer matches the current session timezone, and re-syncs the timezone settings table.
 *
 * `currentOwner` and `currentTimeZone` are passed in (read by the caller) so the caller
 * can key its `useEffect` deps on them and re-run this when the account or timezone
 * changes without unmount.
 *
 * This must be called OUTSIDE of a `useLiveQuery` querier: `useLiveQuery` runs its
 * callback inside a read-only transaction, so any Dexie write from within it throws
 * `ReadOnlyError: Readwrite transaction in liveQuery context`.
 *
 * Correctness notes:
 * - The stored timezone is re-read INSIDE the readwrite transaction, together with the
 *   clear and the timezone put, so a run cannot act on a stored value that another run
 *   (or the service) has already updated -- this prevents clearing freshly-restored cache.
 * - Only the given owner's rows are deleted (owner-scoped), so a timezone mismatch on one
 *   account never wipes another account's cached people.
 * - Concurrent invocations for the same owner + timezone share a single in-flight promise;
 *   different owners or timezones run independently so switches are always checked.
 */
export function clearPlacesPeopleCacheIfTimezoneChanged(
    currentOwner: string,
    currentTimeZone: string | undefined
): Promise<void> {
    const key = `${currentOwner}\u0000${currentTimeZone}`;

    const existing = maintenanceInFlightByKey.get(key);
    if (existing) {
        return existing;
    }

    const run = runPlacesPeopleCacheMaintenance(currentOwner, currentTimeZone).finally(() => {
        maintenanceInFlightByKey.delete(key);
    });
    maintenanceInFlightByKey.set(key, run);

    return run;
}

async function runPlacesPeopleCacheMaintenance(
    currentOwner: string,
    currentTimeZone: string | undefined
): Promise<void> {
    try {
        await db.transaction('rw', placesPeopleTable, peopleTimezoneSettingsTable, async () => {
            // Re-read the stored timezone inside the transaction so the decision is
            // made against the committed state, not a value read before the lock.
            const timezoneSettings = await peopleTimezoneSettingsTable.get(currentOwner);

            if (timezoneSettings) {
                if (timezoneSettings.timezone !== currentTimeZone) {
                    // Stored under a different timezone: drop this owner's rows and
                    // record the current timezone so the mismatch actually resolves.
                    await deleteOwnerRows(currentOwner);
                    await peopleTimezoneSettingsTable.put({
                        uniqueId: currentOwner,
                        timezone: currentTimeZone,
                    });
                }
                return;
            }

            // No stored timezone for this owner. Any existing owner rows have no
            // trustworthy timezone marker (e.g. the timezone put was lost), so drop
            // them, then record the current timezone.
            const staleOwnerRowCount = await placesPeopleTable
                .where('owner')
                .equals(currentOwner)
                .count();

            if (staleOwnerRowCount > 0) {
                await deleteOwnerRows(currentOwner);
            }

            await peopleTimezoneSettingsTable.put({
                uniqueId: currentOwner,
                timezone: currentTimeZone,
            });
        });
    } catch (error) {
        logGreyError(
            'PlacesPeopleCache_clearCacheOnTimezoneMismatch',
            getModuleContextMailboxInfo(),
            error instanceof Error ? error : undefined
        );
    }
}

function deleteOwnerRows(currentOwner: string): Promise<number> {
    return placesPeopleTable.where('owner').equals(currentOwner).delete();
}
